/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import {
  functionsService,
  type FunctionMaster,
  type FunctionIOField,
  type FunctionTestResult,
} from '../../services/functions.service';
import { extractErrorMessage } from '../../utils/error-handler';

/** Only these two types actually execute today; the others come back ok:false. */
const TESTABLE = ['IMPORT_PATH', 'REGISTERED_HANDLER'];

/** dataTypes that hold structured values — edited as JSON, parsed on submit. */
const isStructured = (f: FunctionIOField) =>
  !!f.isCollection || ['object', 'array', 'json'].includes((f.dataType || '').toLowerCase());
const isNumber = (f: FunctionIOField) =>
  ['number', 'integer', 'int', 'float', 'double'].includes((f.dataType || '').toLowerCase());
const isBoolean = (f: FunctionIOField) =>
  ['boolean', 'bool'].includes((f.dataType || '').toLowerCase());

interface Props {
  fn: FunctionMaster;
  visible: boolean;
  onHide: () => void;
}

const FunctionTestDialog = ({ fn, visible, onHide }: Props) => {
  const toast = useRef<Toast>(null);
  const fields = useMemo<FunctionIOField[]>(() => fn.inputDefinition || [], [fn.inputDefinition]);

  const [mode, setMode] = useState<'form' | 'json'>('form');
  // Per-field values. Structured fields hold their raw JSON string; the rest hold
  // the native value (string / number / boolean).
  const [values, setValues] = useState<Record<string, any>>({});
  const [jsonText, setJsonText] = useState<string>('{\n  \n}');
  const [result, setResult] = useState<FunctionTestResult | null>(null);

  const notTestable = !TESTABLE.includes(fn.functionType);

  const setField = (name: string, v: any) => setValues((prev) => ({ ...prev, [name]: v }));

  /** Assemble the params object from whichever editor is active. Throws a readable
   *  message when a JSON field cannot be parsed, so the tester sees the field name. */
  const buildParams = (): Record<string, unknown> => {
    if (mode === 'json') {
      const raw = jsonText.trim();
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('Params must be a JSON object, e.g. { "question": "..." }');
      }
      return parsed;
    }
    const out: Record<string, unknown> = {};
    for (const f of fields) {
      const v = values[f.name];
      if (isStructured(f)) {
        const s = (v ?? '').toString().trim();
        if (!s) continue;
        try {
          out[f.name] = JSON.parse(s);
        } catch {
          throw new Error(`Input '${f.name}' is not valid JSON.`);
        }
      } else if (isBoolean(f)) {
        out[f.name] = !!v;
      } else if (isNumber(f)) {
        if (v !== null && v !== undefined && v !== '') out[f.name] = v;
      } else {
        if (v !== undefined && v !== '') out[f.name] = v;
      }
    }
    return out;
  };

  const mutation = useMutation({
    mutationFn: (params: Record<string, unknown>) => functionsService.test(fn._id, params),
    onSuccess: (res) => setResult(res),
    onError: (e: any) =>
      toast.current?.show({ severity: 'error', summary: 'Test failed', detail: extractErrorMessage(e, 'Could not run test'), life: 8000 }),
  });

  const onRun = () => {
    setResult(null);
    let params: Record<string, unknown>;
    try {
      params = buildParams();
    } catch (e: any) {
      toast.current?.show({ severity: 'warn', summary: 'Invalid input', detail: e?.message || 'Check your inputs', life: 6000 });
      return;
    }
    mutation.mutate(params);
  };

  const renderField = (f: FunctionIOField) => {
    const label = `${f.name}${f.isRequired ? ' *' : ''}`;
    if (isBoolean(f)) {
      return (
        <div key={f.name} className="flex items-center gap-2 py-1">
          <Checkbox inputId={`fld-${f.name}`} checked={!!values[f.name]} onChange={(e) => setField(f.name, e.checked)} />
          <label htmlFor={`fld-${f.name}`} className="text-sm text-gray-700 dark:text-gray-300">{label}</label>
          {f.description ? <span className="text-xs text-gray-400">— {f.description}</span> : null}
        </div>
      );
    }
    if (isNumber(f)) {
      return (
        <div key={f.name} className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
          <InputNumber value={values[f.name] ?? null} onValueChange={(e) => setField(f.name, e.value)} className="w-full" />
          {f.description ? <small className="text-xs text-gray-400">{f.description}</small> : null}
        </div>
      );
    }
    if (isStructured(f)) {
      return (
        <div key={f.name} className="sm:col-span-2">
          <TextArea label={`${label} (JSON)`} value={values[f.name] ?? ""} onChange={(v: string) => setField(f.name, v)} rows={3}
            placeholder={f.isCollection ? '[ ... ]' : '{ ... }'} />
          {f.description ? <small className="text-xs text-gray-400">{f.description}</small> : null}
        </div>
      );
    }
    return (
      <div key={f.name} className="flex flex-col gap-1">
        <Input label={label} value={values[f.name] ?? ""} onChange={(v: string) => setField(f.name, v)} placeholder={f.dataType} />
        {f.description ? <small className="text-xs text-gray-400">{f.description}</small> : null}
      </div>
    );
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button label="Close" severity="secondary" outlined onClick={onHide} />
      <Button label={mutation.isPending ? 'Running…' : 'Run test'}
        icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
        disabled={mutation.isPending} onClick={onRun} />
    </div>
  );

  return (
    <Dialog header={<span className="font-mono">Test · {fn.functionCode}</span>} visible={visible} onHide={onHide}
      style={{ width: '46rem', maxWidth: '95vw' }} footer={footer} dismissableMask>
      <Toast ref={toast} position="top-right" />

      {notTestable && (
        <Message severity="warn" className="w-full mb-3"
          text={`${fn.functionType} functions cannot be executed yet — only IMPORT_PATH and REGISTERED_HANDLER run. Running this will report that.`} />
      )}
      <Message severity="info" className="w-full mb-3"
        text="Values are passed straight to the function as its parameters (parameterMapping is bypassed). Running executes real code." />

      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-gray-500">Input mode:</span>
        <Button label="Form" size="small" text={mode !== 'form'} onClick={() => setMode('form')} />
        <Button label="Raw JSON" size="small" text={mode !== 'json'} onClick={() => setMode('json')} />
      </div>

      {mode === 'form' ? (
        fields.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{fields.map(renderField)}</div>
        ) : (
          <Message severity="secondary" className="w-full"
            text="This function declares no inputs. Switch to Raw JSON to pass params anyway." />
        )
      ) : (
        <TextArea label="Params (JSON object)" value={jsonText} onChange={(v: string) => setJsonText(v)} rows={6}
          placeholder='{ "question": "What is ...?" }' />
      )}

      {result && (
        <div className="mt-4 border-t border-line pt-3">
          <div className="flex items-center gap-2 mb-2">
            <Tag value={result.ok ? 'OK' : 'FAILED'} severity={result.ok ? 'success' : 'danger'} />
            <span className="text-xs text-gray-500">{result.durationMs} ms · {result.functionType}</span>
          </div>
          {result.error && (
            <Message severity="error" className="w-full mb-2"
              text={`${result.error}${result.errorCode ? ` (${result.errorCode})` : ''}`} />
          )}
          {result.ok && (
            <div>
              <label className="text-xs text-ink-muted block mb-1">Output</label>
              <pre className="text-sm text-ink whitespace-pre-wrap break-words font-mono bg-canvas dark:bg-gray-900 rounded p-2 max-h-72 overflow-auto">
                {JSON.stringify(result.output, null, 2)}
              </pre>
            </div>
          )}
          {result.retrievalContext?.length ? (
            <div className="mt-2">
              <label className="text-xs text-ink-muted block mb-1">Retrieval context</label>
              <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words font-mono bg-canvas dark:bg-gray-900 rounded p-2 max-h-48 overflow-auto">
                {JSON.stringify(result.retrievalContext, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  );
};

export default FunctionTestDialog;
